import { useState } from "react"
import { useDispatch } from "react-redux"
import { StoreDispatch } from "../../store/configure-store"
import { FormInputText } from "../../cmps/forms/input/form-input-text"
import { FromDropdown } from "../../cmps/forms/input/form-dropdown"
import { Backlog } from "../../cmps/util-cmps/backlog"
import { Spinner } from "../../cmps/util-cmps/spinner"
import { UserMsg } from "../../models/user"
import { setUserMsg } from "../../store/user"
import { newHttpService } from "../../services/new-http-service"
import { createCompanyMutation } from "../../cmps/util-cmps/graphql-mutations/create-company"
import { updateCompanyMutation } from "../../cmps/util-cmps/graphql-mutations/update-company"
import { useEscapeKey } from "../../hooks/use-escape-key"
import { useScrollLock } from "../../hooks/use-scroll-lock"
import { useFocusTrap } from "../../hooks/use-focus-trap"
import { useDraggable } from "../../hooks/use-draggable"

interface CompanyFormModalProps {
    companyToEdit?: any
    existingNames?: string[]
    onSuccess: () => void
    onClose: () => void
}

const boolOptions = [
    { id: 'false', name: 'False' },
    { id: 'true', name: 'True' },
]

export const CompanyFormModal: React.FC<CompanyFormModalProps> = ({
    companyToEdit, existingNames = [], onSuccess, onClose
}) => {
    const isEditMode = !!companyToEdit
    const dispatch = useDispatch<StoreDispatch>()
    const [isSubmitting, setIsSubmitting] = useState(false)

    const [form, setForm] = useState(() => isEditMode ? {
        name: companyToEdit.name || '',
        abbreviation: companyToEdit.abbreviation || '',
        logoUrl: companyToEdit.logoUrl || '',
        isSilo: String(companyToEdit.isSilo ?? false),
    } : {
        name: '',
        abbreviation: '',
        logoUrl: '',
        isSilo: 'false',
    })

    useEscapeKey(onClose)
    useScrollLock()
    const trapRef = useFocusTrap<HTMLDivElement>()
    const { modalRef, onHeaderMouseDown } = useDraggable<HTMLElement>()

    const onChangeText = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target
        setForm(prev => ({ ...prev, [name]: value }))
    }

    const onChangeDropdown = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const { name, value } = e.target
        setForm(prev => ({ ...prev, [name]: value }))
    }

    const validate = (): string | null => {
        if (!form.name.trim()) return 'Name is required'
        if (!form.abbreviation.trim()) return 'Abbreviation is required'
        if (!isEditMode && existingNames.some(n => n.toLowerCase() === form.name.trim().toLowerCase())) {
            return `Company "${form.name.trim()}" already exists`
        }
        return null
    }

    const onSubmit = async () => {
        const error = validate()
        if (error) {
            const msg: UserMsg = { type: 'danger', txt: error }
            dispatch(setUserMsg({ msg }))
            return
        }

        setIsSubmitting(true)
        try {
            if (isEditMode) {
                const query = updateCompanyMutation()
                const variables = {
                    companyId: Number(companyToEdit.id),
                    name: form.name.trim(),
                    abbreviation: form.abbreviation.trim(),
                    logoUrl: form.logoUrl.trim() || null,
                    isSilo: form.isSilo === 'true',
                }
                const res = await newHttpService.graphql('auditech', { query, variables })
                console.log('updateCompany response:', JSON.stringify(res))
                if (!res?.updateCompany?.company) throw new Error('Update failed')
                const msg: UserMsg = { type: 'success', txt: 'Company updated successfully' }
                dispatch(setUserMsg({ msg }))
            } else {
                const query = createCompanyMutation(
                    form.name.trim(),
                    form.abbreviation.trim(),
                    form.logoUrl.trim() || null,
                    form.isSilo === 'true'
                )
                const res = await newHttpService.graphql('auditech', query)
                console.log('createCompany response:', JSON.stringify(res))
                if (!res?.createCompany?.company) throw new Error('Create failed')
                const msg: UserMsg = { type: 'success', txt: 'Company created successfully' }
                dispatch(setUserMsg({ msg }))
            }
            onSuccess()
        } catch (err) {
            console.error('Company mutation error:', err)
            const msg: UserMsg = { type: 'danger', txt: isEditMode ? 'Failed updating company' : 'Failed creating company' }
            dispatch(setUserMsg({ msg }))
        } finally {
            setIsSubmitting(false)
        }
    }

    const modalTitle = isEditMode ? 'Edit Company' : 'Create Company'

    return <div ref={trapRef}>
        <section ref={modalRef} className="company-form-modal" role="dialog" aria-modal="true" aria-label={modalTitle}>
            <header onMouseDown={onHeaderMouseDown}>
                <h2>{modalTitle}</h2>
                <button className="close-btn" onClick={onClose} aria-label="Close">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="18" y1="6" x2="6" y2="18" />
                        <line x1="6" y1="6" x2="18" y2="18" />
                    </svg>
                </button>
            </header>

            <section className="modal-form-body">
                <div className="form-section-card">
                    <div className="form-section-label">Company Details</div>
                    <div className="form-row">
                        <div className="field">
                            <FormInputText
                                keyName="name"
                                label="Name"
                                isRequired={!isEditMode}
                                isDisabled={isEditMode}
                                value={form.name}
                                onChange={onChangeText}
                            />
                        </div>
                        <div className="field">
                            <FormInputText
                                keyName="abbreviation"
                                label="Abbreviation"
                                isRequired={!isEditMode}
                                isDisabled={isEditMode}
                                value={form.abbreviation}
                                onChange={onChangeText}
                            />
                        </div>
                        <div className="field">
                            <FormInputText
                                keyName="logoUrl"
                                label="Logo URL"
                                isRequired={false}
                                value={form.logoUrl}
                                onChange={onChangeText}
                            />
                        </div>
                        <div className="field">
                            <FromDropdown
                                keyName="isSilo"
                                label="Is Silo"
                                options={boolOptions}
                                value={form.isSilo}
                                onSelectValue={onChangeDropdown}
                                hideBlankOption={true}
                            />
                        </div>
                    </div>
                </div>

                <div className="form-actions">
                    {!isSubmitting && <button className="main" onClick={onSubmit} disabled={isSubmitting}>
                        {isEditMode ? 'Save' : 'Create'}
                    </button>}
                    {isSubmitting && <span className="spinner"><Spinner isSmall={true} /></span>}
                </div>
            </section>
        </section>
        <Backlog zIndex={199} onCloseBacklog={onClose} />
    </div>
}
